'use client';

/**
 * MessageReactionPanel — the optional-detail panel that opens under a
 * message's reaction row (message-reaction-row.tsx), in one of two variants:
 *
 *   - REASON: opened by tapping 👎 (the down-vote itself already "sent" —
 *     see message-reaction-view.ts's header — this panel is a pure
 *     enrichment: reason chips first, free text second, per the source
 *     doc's "detail is always optional" principle).
 *   - NOTE: opened by the standalone note button, no thumb required at all
 *     ("a thumb is optional" is the label copy itself, not an incidental
 *     omission — see message-reaction-row.tsx's header comment on why this
 *     is a designed state).
 *
 * Purely presentational — all state (selected reasons, comment text,
 * submitting/error) lives in the container (message-reaction-row.tsx),
 * same split as HandoffTimeoutMessage/-Callback take from their panel
 * container.
 */
import { cn } from '@/lib/utils';

export const MESSAGE_REACTION_REASONS = [
  'Not accurate',
  "Didn't help",
  'Too long',
  'Felt off',
  'Something else',
] as const;

export const MessageReactionPanelVariant = {
  REASON: 'reason',
  NOTE: 'note',
} as const;
export type MessageReactionPanelVariant =
  (typeof MessageReactionPanelVariant)[keyof typeof MessageReactionPanelVariant];

interface MessageReactionPanelProps {
  variant: MessageReactionPanelVariant;
  selectedReasons: string[];
  onToggleReason: (reason: string) => void;
  comment: string;
  onCommentChange: (value: string) => void;
  onSkip: () => void;
  onSend: () => void;
  submitting: boolean;
  error?: string | null;
}

export function MessageReactionPanel({
  variant,
  selectedReasons,
  onToggleReason,
  comment,
  onCommentChange,
  onSkip,
  onSend,
  submitting,
  error,
}: MessageReactionPanelProps) {
  const isReason = variant === MessageReactionPanelVariant.REASON;

  return (
    <div
      className={cn(
        'ml-[36px] flex flex-col gap-[11px] rounded-[12px] border-[1px] border-[#eadfce] bg-[#FBF7F1] px-[15px] py-[14px]',
        // Reuses the existing Turn-16 "rise" keyframe (tailwind.config.ts's
        // call-rise: translateY(9px)+opacity, ease-out, `both` fill) rather
        // than adding a duplicate — motion-safe: is the same
        // prefers-reduced-motion gate every other consumer of it uses, so a
        // reduced-motion user just gets the panel at its resting state with
        // no animation at all.
        'motion-safe:animate-call-rise'
      )}
    >
      <div className="text-[12.5px] font-semibold text-[#7a7062]">
        {isReason ? 'What was off?' : 'Leave a note'}{' '}
        <span className="font-normal text-[#a99e8c]">
          {isReason ? '— optional' : '— a thumb is optional'}
        </span>
      </div>

      {isReason && (
        <div
          className="flex flex-wrap gap-[6px]"
          role="group"
          aria-label="Reason"
        >
          {MESSAGE_REACTION_REASONS.map((reason) => {
            const selected = selectedReasons.includes(reason);
            return (
              <button
                key={reason}
                type="button"
                onClick={() => onToggleReason(reason)}
                aria-pressed={selected}
                className={cn(
                  'rounded-[20px] border-[1px] px-[12px] py-[6px] text-[12px] font-medium',
                  selected
                    ? 'border-[#E7B387] bg-[#FFF3E5] text-[#B0684A]'
                    : 'border-[#e0d6c6] bg-white text-[#6b6152]'
                )}
              >
                {reason}
              </button>
            );
          })}
        </div>
      )}

      <textarea
        value={comment}
        onChange={(e) => onCommentChange(e.target.value)}
        placeholder="Tell Sara more…"
        aria-label={isReason ? 'Tell Sara more' : 'Your note'}
        className={cn(
          'rounded-[9px] border-[1px] border-[#e6ddce] bg-white px-[11px] py-[9px] text-[12.5px]',
          'placeholder:text-[#b3a996]',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#e36001]/25',
          isReason ? 'min-h-[40px]' : 'min-h-[52px]'
        )}
      />

      {error && (
        <div role="alert" className="text-[12px] text-[#b3261e]">
          {error}
        </div>
      )}

      <div className="flex items-center justify-end gap-[14px]">
        <button
          type="button"
          onClick={onSkip}
          disabled={submitting}
          className="text-[12.5px] font-medium text-[#a99e8c] disabled:opacity-50"
        >
          {isReason ? 'Skip' : 'Cancel'}
        </button>
        <button
          type="button"
          onClick={onSend}
          disabled={submitting}
          className={cn(
            'rounded-[9px] px-[18px] py-[8px] text-[12.5px] font-semibold text-white disabled:opacity-60',
            isReason ? 'bg-[#E36001]' : 'bg-[#31364E]'
          )}
        >
          {submitting ? 'Sending…' : isReason ? 'Send' : 'Send note'}
        </button>
      </div>
    </div>
  );
}
